import React from 'react';
import { Panel } from '@tanium/react-panel';
import { DefinitionList, DefinitionListItem } from '@tanium/react-definition-list';
import { useTranslation } from 'react-i18next';
import MediumText from 'components/styles/text/MediumText';
import MediumBoldText from 'components/styles/text/MediumBoldText';
import Helper from 'utils/Helper';
import { Link } from '@tanium/react-link';
import { AWS_ACCOUNT_ROUTE, CLUSTER_ROUTE } from 'Route';
import Customer, { CloudProvider } from 'gql/Customer';

export interface ICustomerCloudDetailsProps {
  customer?: Customer;
}

export const CustomerCloudDetails = ({ customer }: ICustomerCloudDetailsProps): React.ReactElement => {
  const { t } = useTranslation();

  const getClusterInstanceDescription = () => {
    const link = getClusterLink(customer);
    const children = <MediumBoldText>{customer?.clusterInstanceName || '-'}</MediumBoldText>;
    return link ? <Link href={link}>{children}</Link> : children;
  };

  const getCloudAccountDescription = () => {
    const link = getCloudAccountLink(customer);
    const children = <MediumBoldText>{customer?.cloudAccountId || '-'}</MediumBoldText>;
    return link ? <Link href={link}>{children}</Link> : children;
  };

  return (
    <Panel title={t('CLOUD_INFO')}>
      <DefinitionList>
        <DefinitionListItem
          term={<MediumText>{t('CLOUD_PROVIDER')}:</MediumText>}
          description={<MediumBoldText>{customer?.cloudProvider || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('CLOUD_ACCOUNT_ID')}:</MediumText>}
          description={getCloudAccountDescription()}
        />
        <DefinitionListItem
          term={<MediumText>{t('INSTANCE_NAME')}:</MediumText>}
          description={<MediumBoldText>{customer?.instanceName || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('CLUSTER_INSTANCE_NAME')}:</MediumText>}
          description={getClusterInstanceDescription()}
        />
        <DefinitionListItem
          term={<MediumText>{t('REGION')}:</MediumText>}
          description={<MediumBoldText>{customer?.region || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('WORKSPACE')}:</MediumText>}
          description={<MediumBoldText>{customer?.AwsWorkspacePrefix || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('ACCESS_REQUIRES_BASTION')}:</MediumText>}
          description={<MediumBoldText>{customer?.accessRequiresBastion ? 'true' : 'false'}</MediumBoldText>}
        />
      </DefinitionList>
    </Panel>
  );
};

export const getClusterLink = (customer?: Customer) => {
  if (!customer?.clusterId) {
    return '';
  }
  return Helper.interpolateRoute(CLUSTER_ROUTE.url, {
    clusterId: customer?.clusterId,
  });
};

export const getCloudAccountLink = (customer?: Customer) => {
  switch (customer?.cloudProvider) {
    case CloudProvider.AWS:
    case CloudProvider.AWS_OCI:
    case CloudProvider.GOV_AWS: {
      return Helper.interpolateRoute(AWS_ACCOUNT_ROUTE.url, {
        awsAccountObjectId: `awsaccount_${customer.cloudAccountId}`,
      });
    }
  }

  return '';
};

export default CustomerCloudDetails;
